import { useEffect, useRef, useState } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs"
import { Button } from "components/ui/button"
import { Card, CardContent } from "components/ui/card"
import { Skeleton } from "components/ui/skeleton"
import { Upload, Video, FileText } from "lucide-react"
import { useNavigate } from "react-router-dom"

export default function VideoUploadPage({ file, setFile, activeTab, setActiveTab, setVideoID }) {
  const [previewUrl, setPreviewUrl] = useState(null)
  const [isGuideLoaded, setIsGuideLoaded] = useState(false)
  const videoRef = useRef(null)
  const fileInputRef = useRef(null)
  const navigate = useNavigate()

  const handleLoadedData = () => {
    setIsGuideLoaded(true)
  }

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file)
      setPreviewUrl(url)
      return () => URL.revokeObjectURL(url)
    }
  }, [file])

  const handleRemoveFile = () => {
    setFile(null)
    setPreviewUrl(null)
    // Reset the file input value
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0]
    if (selectedFile) {
      setFile(selectedFile)
    }
  }

  return (
    <div className="bg-gray-50 relative">
      <div className="container mx-auto p-8">
        <Card className="w-full max-w-6xl mx-auto border-0">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-2/5 flex flex-col items-center justify-between">
                <div className="flex">
                  {file ? (
                    <div className="w-full flex flex-col items-center justify-center">
                      <div className="aspect-square max-w-full md:max-w-64 rounded-lg overflow-hidden shadow-[5px_5px_1px_1px_rgba(109,109,217)]">
                        <video src={previewUrl} className="w-full h-full object-cover" controls muted />
                      </div>
                      <Button
                        onClick={handleRemoveFile}
                        className="mt-3 bg-red-400 text-white hover:bg-red-500 self-center rounded-full"
                      >
                        Remove Video
                      </Button>
                    </div>
                  ) : (
                    <div
                      className="aspect-square border-2 border-dashed rounded-3xl p-4 md:p-8 text-center cursor-pointer transition-colors h-48 md:h-64 flex items-center justify-center border-gray-300"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <div className="space-y-4">
                        <Upload className="mx-auto text-gray-400" size={48} />
                        <p className="text-gray-500">
                          Click to select a video file, or drag and drop anywhere on the screen
                        </p>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileSelect}
                          accept="video/*"
                          className="hidden"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="w-full md:w-3/5 mt-6 md:mt-0">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full md:grid-cols-2 grid-cols-1 mb-24 md:mb-5 gap-3">
                    <TabsTrigger
                      value="video"
                      className={`flex items-center py-3 gap-2 rounded-full bg-gray-200 ${activeTab === "video" ? "bg-gray-400" : ""}`}
                    >
                      <Video size={16} />
                      Video Instructions
                    </TabsTrigger>
                    <TabsTrigger
                      value="text"
                      className={`flex items-center py-3 gap-2 rounded-full bg-gray-200 ${activeTab === "text" ? "bg-gray-400" : ""}`}
                    >
                      <FileText size={16} />
                      Text Instructions
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="video" className="mt-0">
                    <div className="aspect-video bg-black rounded-lg overflow-hidden">
                      {!isGuideLoaded && <Skeleton className="w-full h-full bg-gray-300" />}
                      <video
                        ref={videoRef}
                        controls
                        controlsList="nodownload"
                        autoPlay
                        muted
                        className="w-full h-full object-cover"
                        src="https://assets-editai.azureedge.net/videos-webm/editai-marketer-suite-instructions.webm"
                        onLoadedData={handleLoadedData}
                        style={{ display: activeTab === "video" ? "block" : "none" }}
                      />
                    </div>
                  </TabsContent>
                  <TabsContent value="text" className="mt-0">
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h3 className="text-lg font-semibold mb-2">Recommended</h3>
                          <ul className="list-disc pl-5 space-y-1 text-sm">
                            <li>15-90 seconds of footage</li>
                            <li>Keep your head centered in the frame</li>
                            <li>Ensure face visibility throughout the entire video</li>
                            <li>Well-lit quiet environment</li>
                            <li>Use a tripod or stabilize your phone to avoid shaking</li>
                            <li>Supported resolutions: 720x1280, 1080x1920, or 2160x3840</li>
                          </ul>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold mb-2">Things to avoid</h3>
                          <ul className="list-disc pl-5 space-y-1 text-sm">
                            <li>Pre-edited footage</li>
                            <li>Talking with pauses</li>
                            <li>Loud background noise like music</li>
                            <li>Shadows or overexposure on your face</li>
                            <li>Diverting your gaze or looking around</li>
                            <li>Leaving the frame or hiding your face</li>
                          </ul>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-2">💡 Tips</h3>
                        <p className="text-sm">
                          To make your video lively and expressive, exaggerate your emotions when speaking. Feel free to
                          talk about any topic in the language of your choice. Be yourself, look into the camera, and we
                          will take care of the rest 😊
                        </p>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
